import React from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { ThemeProvider } from "./contexts/ThemeContext";
import { Toaster } from "./components/ui/sonner";
import ProtectedRoute from "./components/ProtectedRoute";
import UnderConstruction from "./components/UnderConstruction";
import IdleScreen from "./components/IdleScreen";
import ConciergeChat from "./components/ConciergeChat";
import SecurityRulesModal, { useSecurityRulesModal } from "./components/SecurityRulesModal";
import HumanoPontuaisAgent from "./components/HumanoPontuaisAgent";

// Pages
import LandingPage from "./pages/LandingPage";
import Login from "./pages/Login";
import AuthCallback from "./pages/AuthCallback";
import ResetPassword from "./pages/ResetPassword";
import Dashboard from "./pages/Dashboard";
import NovoLivro from "./pages/NovoLivro";
import MeusLivros from "./pages/MeusLivros";
import LivroEstrutura from "./pages/LivroEstrutura";
import EditorCapitulo from "./pages/EditorCapitulo";
import PerfilDoAutor from "./pages/PerfilDoAutor";
import Configuracoes from "./pages/Configuracoes";
import BauDeInspiracoes from "./pages/BauDeInspiracoes";
import Frases from "./pages/Frases";
import ConviteEscritor from "./pages/ConviteEscritor";
import EmuladorAcelerador from "./pages/EmuladorAcelerador";
import ConselhoAgentes from "./pages/ConselhoAgentes";
import ArquivoIntelectual from "./pages/ArquivoIntelectual";
import AdminSaudeApp from "./pages/AdminSaudeApp";
import SecuritySettings from "./pages/SecuritySettings";
import JornadaAutor from "./pages/JornadaAutor";
import VerificarCertificado from "./pages/VerificarCertificado";
import Unsubscribe from "./pages/Unsubscribe";
import VerificarEmail from "./pages/VerificarEmail";
import EntryExperience from "./pages/EntryExperience";
import CafeteriaBestSeller from "./pages/CafeteriaBestSeller";
import Configurar2FA from "./pages/Configurar2FA";
import UpgradePage from "./pages/UpgradePage";

function AppRouter() {
  const location = useLocation();
  
  // Check URL fragment for session_id synchronously during render
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }
  
  return (
    <Routes>
      <Route path="/" element={<LandingPage />} />
      <Route path="/login" element={<Login />} />
      <Route path="/auth/callback" element={<AuthCallback />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/verificar/:hash" element={<VerificarCertificado />} />
      <Route path="/verificar-email" element={<VerificarEmail />} />
      <Route path="/unsubscribe" element={<Unsubscribe />} />
      <Route path="/configurar-2fa" element={<Configurar2FA />} />
      
      {/* Protected Routes */}
      <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
      <Route path="/novo-livro" element={<ProtectedRoute><NovoLivro /></ProtectedRoute>} />
      <Route path="/editar-livro/:bookId" element={<ProtectedRoute><NovoLivro /></ProtectedRoute>} />
      <Route path="/meus-livros" element={<ProtectedRoute><MeusLivros /></ProtectedRoute>} />
      
      {/* Profile & Settings */}
      <Route path="/perfil-do-autor" element={<ProtectedRoute><PerfilDoAutor /></ProtectedRoute>} />
      <Route path="/configuracoes" element={<ProtectedRoute><Configuracoes /></ProtectedRoute>} />
      
      {/* Book Routes */}
      <Route path="/livro/:bookId" element={<ProtectedRoute><LivroEstrutura /></ProtectedRoute>} />
      <Route path="/editor/:chapterId" element={<ProtectedRoute><EditorCapitulo /></ProtectedRoute>} />
      
      {/* Tools */}
      <Route path="/inspiracoes" element={<ProtectedRoute><BauDeInspiracoes /></ProtectedRoute>} />
      <Route path="/frases" element={<ProtectedRoute><Frases /></ProtectedRoute>} />
      <Route path="/arquivo-intelectual" element={<ProtectedRoute><ArquivoIntelectual /></ProtectedRoute>} />
      <Route path="/bau-intelectual" element={<ProtectedRoute><ArquivoIntelectual /></ProtectedRoute>} />
      <Route path="/emulador-acelerador" element={<ProtectedRoute><EmuladorAcelerador /></ProtectedRoute>} />
      <Route path="/conselho-agentes" element={<ProtectedRoute><ConselhoAgentes /></ProtectedRoute>} />
      <Route path="/cafeteria" element={<ProtectedRoute><CafeteriaBestSeller /></ProtectedRoute>} />
      <Route path="/progresso-criativo" element={<ProtectedRoute><JornadaAutor /></ProtectedRoute>} />
      <Route path="/jornada-do-autor" element={<ProtectedRoute><JornadaAutor /></ProtectedRoute>} />
      <Route path="/entrada" element={<ProtectedRoute><EntryExperience /></ProtectedRoute>} />
      
      {/* Premium - Em construção */}
      <Route path="/empresario-literario" element={<ProtectedRoute><UnderConstruction title="Empresário Literário" description="Recurso Premium - Em breve: Planejamento estratégico da sua escrita" /></ProtectedRoute>} />
      
      {/* Admin - Has its own login */}
      <Route path="/comando/saudeapp" element={<AdminSaudeApp />} />
      
      {/* Security Settings */}
      <Route path="/seguranca" element={<ProtectedRoute><SecuritySettings /></ProtectedRoute>} />
      
      {/* Upgrade Page */}
      <Route path="/upgrade" element={<ProtectedRoute><UpgradePage /></ProtectedRoute>} />
      
      {/* Convite de Colega Escritor */}
      <Route path="/convite/:token" element={<ConviteEscritor />} />
      
      {/* Catch all */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

// Idle screen wrapper - only shows for authenticated users
function IdleScreenWrapper() {
  const { user } = useAuth();
  const location = useLocation();
  
  // Don't show idle screen on public pages
  const publicPaths = ['/', '/login', '/auth/callback', '/reset-password', '/verificar', '/configurar-2fa'];
  const isPublicPage = publicPaths.some(path => location.pathname.startsWith(path));
  
  if (!user || isPublicPage) return null;
  
  return <IdleScreen />;
}

// Security rules modal wrapper - shows once for authenticated users
function SecurityRulesWrapper() {
  const { user } = useAuth();
  const { showModal, closeModal } = useSecurityRulesModal();
  
  if (!user) return null;
  
  return (
    <SecurityRulesModal 
      isOpen={showModal} 
      onClose={closeModal}
      onAccept={() => {}}
    />
  );
}

// Concierge chat wrapper - shows on all pages for everyone
function ConciergeWrapper() {
  return <ConciergeChat />;
}

function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <BrowserRouter>
          <AppRouter />
          <IdleScreenWrapper />
          <SecurityRulesWrapper />
          <ConciergeWrapper />
          <HumanoPontuaisAgent />
          <Toaster />
        </BrowserRouter>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
